import { useEffect, useMemo, useRef, useState } from 'react'
import { FileImage, FileUp, RotateCw, Trash2, X } from 'lucide-react'
import { useEditorStore } from '../../store/editorStore'
import { calibrateUnderlay, getUnderlayCrop, validateUnderlayCalibration, type BackgroundUnderlay, type UnderlayKind, type UnderlayOrdering } from '../../utils/underlayCalibration.logic'

type Props = { onClose: () => void }

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 8px', border: '1px solid var(--border, #1a2744)', borderRadius: 4,
  background: 'var(--bg-input, #111827)', color: 'var(--text-primary, #f3f4f6)', fontSize: 12,
}

export function BackgroundCalibrationModal({ onClose }: Props) {
  const stored = useEditorStore(state => state.dcSettings.backgroundUnderlay)
  const setDcSettings = useEditorStore(state => state.setDcSettings)
  const [draft, setDraft] = useState<BackgroundUnderlay>(() => stored || {
    kind: 'image', source: '', name: '', sourceWidthPx: 1000, sourceHeightPx: 1000,
    referencePixels: 1000, referenceDistanceM: 10, originXMm: 0, originYMm: 0,
    rotationDeg: 0, opacity: 0.45, ordering: 'behind-grid',
    crop: { xPx: 0, yPx: 0, widthPx: 1000, heightPx: 1000 },
  })
  const [error, setError] = useState('')
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => { closeButtonRef.current?.focus() }, [])

  useEffect(() => {
    if (draft.kind !== 'image' || !draft.source) return
    const image = new window.Image()
    image.onload = () => {
      if (!image.naturalWidth || !image.naturalHeight) return
      setDraft(current => ({
        ...current,
        sourceWidthPx: image.naturalWidth,
        sourceHeightPx: image.naturalHeight,
        crop: current.crop.widthPx === current.sourceWidthPx && current.crop.heightPx === current.sourceHeightPx
          ? { xPx: 0, yPx: 0, widthPx: image.naturalWidth, heightPx: image.naturalHeight }
          : current.crop,
      }))
    }
    image.src = draft.source
  }, [draft.kind, draft.source])

  const calibration = useMemo(() => {
    try { return calibrateUnderlay(draft) } catch { return null }
  }, [draft])

  const update = <K extends keyof BackgroundUnderlay>(key: K, value: BackgroundUnderlay[K]) => setDraft(current => ({ ...current, [key]: value }))
  const updateCrop = (key: keyof BackgroundUnderlay['crop'], value: number) => setDraft(current => {
    const crop = { ...current.crop, [key]: Math.max(0, value) }
    if (key === 'xPx') crop.widthPx = Math.min(crop.widthPx, Math.max(1, current.sourceWidthPx - crop.xPx))
    if (key === 'yPx') crop.heightPx = Math.min(crop.heightPx, Math.max(1, current.sourceHeightPx - crop.yPx))
    if (key === 'widthPx') crop.widthPx = Math.min(crop.widthPx, Math.max(1, current.sourceWidthPx - crop.xPx))
    if (key === 'heightPx') crop.heightPx = Math.min(crop.heightPx, Math.max(1, current.sourceHeightPx - crop.yPx))
    return { ...current, crop }
  })

  const readFile = (file?: File) => {
    if (!file) return
    const kind: UnderlayKind = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image'
    const reader = new FileReader()
    reader.onload = () => updateDraftSource(String(reader.result || ''), kind, file.name)
    reader.readAsDataURL(file)
  }

  const updateDraftSource = (source: string, kind: UnderlayKind, name = draft.name) => {
    setDraft(current => ({ ...current, source, kind, name, crop: { xPx: 0, yPx: 0, widthPx: current.sourceWidthPx, heightPx: current.sourceHeightPx } }))
  }

  const save = () => {
    try {
      const crop = getUnderlayCrop(draft)
      validateUnderlayCalibration({ ...draft, crop })
      setDcSettings({ backgroundUnderlay: { ...draft, crop } })
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Проверьте параметры подложки')
    }
  }

  return (
    <div className="editor-underlay-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
      <section className="editor-underlay-modal" role="dialog" aria-modal="true" aria-labelledby="underlay-title" onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); onClose() } }}>
        <header className="editor-underlay-header">
          <div><strong id="underlay-title"><FileImage size={15} /> Подложка и калибровка</strong><small>Подложка визуальная и не участвует в коллизиях.</small></div>
          <button ref={closeButtonRef} type="button" className="editor-icon-button" aria-label="Закрыть настройку подложки" onClick={onClose}><X size={15} /></button>
        </header>

        <div className="editor-underlay-body">
          <div className="editor-underlay-source-row">
            <label>Файл подложки<input type="file" accept="image/*,.pdf,application/pdf" onChange={event => readFile(event.target.files?.[0])} /></label>
            <label>Или URL<input style={inputStyle} value={draft.source.startsWith('data:') ? '' : draft.source} placeholder="https://.../plan.png" onChange={event => updateDraftSource(event.target.value, event.target.value.toLowerCase().includes('.pdf') ? 'pdf' : 'image', event.target.value.split('/').pop() || 'Подложка')} /></label>
          </div>
          <div className="editor-underlay-file-state"><FileUp size={13} /> {draft.name || (draft.source ? 'Источник задан' : 'Источник не выбран')}</div>

          <div className="editor-underlay-grid">
            <label>Ширина исходника, px<input style={inputStyle} type="number" min="1" value={draft.sourceWidthPx} onChange={event => update('sourceWidthPx', Number(event.target.value))} /></label>
            <label>Высота исходника, px<input style={inputStyle} type="number" min="1" value={draft.sourceHeightPx} onChange={event => update('sourceHeightPx', Number(event.target.value))} /></label>
            <label>Эталон на плане, px<input style={inputStyle} type="number" min="1" value={draft.referencePixels} onChange={event => update('referencePixels', Number(event.target.value))} /></label>
            <label>Реальная дистанция, м<input style={inputStyle} type="number" min="0.01" step="0.01" value={draft.referenceDistanceM} onChange={event => update('referenceDistanceM', Number(event.target.value))} /></label>
          </div>
          {calibration && <div className="editor-underlay-calibration"><strong>{calibration.widthM.toFixed(2)} × {calibration.heightM.toFixed(2)} м</strong><span>1 px = {(calibration.metresPerPixel * 1000).toFixed(2)} мм</span></div>}

          <div className="editor-underlay-grid">
            <label>Origin X, мм<input style={inputStyle} type="number" value={draft.originXMm} onChange={event => update('originXMm', Number(event.target.value))} /></label>
            <label>Origin Y, мм<input style={inputStyle} type="number" value={draft.originYMm} onChange={event => update('originYMm', Number(event.target.value))} /></label>
            <label>Поворот, °<span className="editor-underlay-inline-input"><RotateCw size={13} /><input style={inputStyle} type="number" step="0.1" value={draft.rotationDeg} onChange={event => update('rotationDeg', Number(event.target.value))} /></span></label>
            <label>Прозрачность<input type="range" min="0" max="1" step="0.05" value={draft.opacity} onChange={event => update('opacity', Number(event.target.value))} /><span className="editor-underlay-range-value">{Math.round(draft.opacity * 100)}%</span></label>
          </div>

          <div className="editor-underlay-grid">
            <label>Порядок отображения<select style={inputStyle} value={draft.ordering} onChange={event => update('ordering', event.target.value as UnderlayOrdering)}><option value="behind-grid">Под сеткой</option><option value="above-plan">Поверх плана</option></select></label>
            <label>Crop X, px<input style={inputStyle} type="number" min="0" value={draft.crop.xPx} onChange={event => updateCrop('xPx', Number(event.target.value))} /></label>
            <label>Crop Y, px<input style={inputStyle} type="number" min="0" value={draft.crop.yPx} onChange={event => updateCrop('yPx', Number(event.target.value))} /></label>
            <label>Crop W × H, px<span className="editor-underlay-inline-input"><input style={inputStyle} type="number" min="1" value={draft.crop.widthPx} onChange={event => updateCrop('widthPx', Number(event.target.value))} /><input style={inputStyle} type="number" min="1" value={draft.crop.heightPx} onChange={event => updateCrop('heightPx', Number(event.target.value))} /></span></label>
          </div>
          {error && <p className="editor-underlay-error" role="alert">{error}</p>}
        </div>

        <footer className="editor-underlay-footer">
          {stored && <button type="button" className="editor-underlay-remove" onClick={() => { setDcSettings({ backgroundUnderlay: null }); onClose() }}><Trash2 size={13} /> Убрать подложку</button>}
          <span />
          <button type="button" className="editor-underlay-cancel" onClick={onClose}>Отмена</button>
          <button type="button" className="editor-underlay-save" onClick={save}>Применить к черновику</button>
        </footer>
      </section>
    </div>
  )
}
